import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { listAssignmentsResult } from '@/modules/deals/services';
import {
  archiveStaffMember,
  buildStaffOperationalReadModel,
  createStaffMember,
  deactivateStaffMember,
  deleteStaffMemberPermanently,
  listStaffResult,
  reactivateStaffMember,
  updateStaffMember,
} from '@/modules/staff/services';
import { isInternalStaff } from '../utils/staff.helpers';
import { getInitialStaffForm } from '@/modules/staff/utils';
import { toast } from '../../../shared/hooks/use-toast';

const STAFF_AUTH_REQUIRED_ERRORS = new Set([
  'STAFF_AUTH_REQUIRED',
  'STAFF_SESSION_REFRESH_REQUIRED',
  'STAFF_SESSION_EXPIRED',
]);

export function useStaffData(onOpenDetails) {
  const {
    capabilities: { canCreateStaff, canManageStaffDestructiveActions },
    logout,
  } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [staffList, setStaffList] = useState([]);
  const [newStaff, setNewStaff] = useState(getInitialStaffForm());
  const [isLoadingStaff, setIsLoadingStaff] = useState(true);
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffAuthRequired, setStaffAuthRequired] = useState(false);
  const [staffModuleDisabled, setStaffModuleDisabled] = useState(false);
  const [staffTenantDatabaseBlocked, setStaffTenantDatabaseBlocked] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [staffAssignmentsStatus, setStaffAssignmentsStatus] = useState('loading');
  const [staffAssignmentsError, setStaffAssignmentsError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadStaff() {
      if (!cancelled) {
        setIsLoadingStaff(true);
        setStaffUnavailable(false);
        setStaffModuleDisabled(false);
        setStaffError('');
        setStaffAssignmentsStatus('loading');
        setStaffAssignmentsError('');
      }

      const [result, assignmentsResult] = await Promise.all([
        listStaffResult({ includeInactive: true }),
        listAssignmentsResult(),
      ]);
      if (!cancelled) {
        const authRequired = STAFF_AUTH_REQUIRED_ERRORS.has(result.error);
        const moduleDisabled = result.error === 'STAFF_MODULE_DISABLED';
        const tenantDatabaseBlocked =
          result.error === 'STAFF_TENANT_DATABASE_NOT_READY' ||
          result.error === 'STAFF_TENANT_DATABASE_METADATA_MISSING' ||
          result.error === 'STAFF_TENANT_DATABASE_UNAVAILABLE';
        const assignmentsAuthRequired = assignmentsResult.error === 'ASSIGNMENTS_AUTH_REQUIRED';
        const assignmentsStatus = assignmentsAuthRequired
          ? 'auth_required'
          : !assignmentsResult.ok
            ? 'unavailable'
            : assignmentsResult.data.length > 0
              ? 'available'
              : 'empty';
        const staffWithAssignments = result.ok
          ? buildStaffOperationalReadModel(
              result.data,
              assignmentsResult.ok ? assignmentsResult.data : []
            )
          : [];

        setStaffList(staffWithAssignments);
        setStaffUnavailable(!result.ok && !authRequired && !tenantDatabaseBlocked && !moduleDisabled);
        setStaffAuthRequired(authRequired);
        setStaffModuleDisabled(moduleDisabled);
        setStaffTenantDatabaseBlocked(tenantDatabaseBlocked);
        setStaffError(result.ok ? '' : result.message || result.error || 'Catalogo staff non disponibile dal backend.');
        setStaffAssignmentsStatus(assignmentsStatus);
        setStaffAssignmentsError(assignmentsResult.ok ? '' : assignmentsResult.message || assignmentsResult.error || 'Tasks non disponibili dal backend.');
        setIsLoadingStaff(false);
      }
    }

    loadStaff();

    return () => {
      cancelled = true;
    };
  }, []);


  useEffect(() => {
    const searchQuery = searchParams.get('search');
    if (!searchQuery) return;

    const foundStaff = staffList.find((member) =>
      member.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (foundStaff) {
      onOpenDetails(foundStaff);
    }

    setSearchParams({});
  }, [onOpenDetails, searchParams, setSearchParams, staffList]);

  function updateNewStaffField(field, value) {
    setNewStaff((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function resetNewStaff() {
    setNewStaff(getInitialStaffForm());
  }

  async function createStaff() {
    if (!canCreateStaff) {
      toast({ title: 'Permesso negato', description: 'Non hai i permessi per aggiungere personale.', variant: 'destructive' });
      return false;
    }

    const result = await createStaffMember({
      ...newStaff,
      tipoPersonale: 'esterno',
      accessType: 'external',
      linkedWorkspaceUserId: null,
    });
    if (!result.ok) {
      if (STAFF_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      toast({ title: 'Personale non creato', description: result.message, variant: 'destructive' });
      return false;
    }

    const createdStaffMember = result.data.staffMember;

    setStaffList((prev) => [...prev, createdStaffMember]);
    resetNewStaff();
    toast({ title: 'Personale creato', description: result.message });
    return true;
  }

  async function applyStaffLifecycle(staffId, action) {
    if (!canManageStaffDestructiveActions) {
      toast({ title: 'Permesso negato', description: 'Non hai i permessi per modificare lo stato del personale.', variant: 'destructive' });
      return false;
    }
    const operation = action === 'delete'
      ? deleteStaffMemberPermanently
      : action === 'archive'
      ? archiveStaffMember
      : action === 'reactivate'
        ? reactivateStaffMember
        : deactivateStaffMember;
    const result = await operation(staffId);
    if (!result.ok) {
      if (STAFF_AUTH_REQUIRED_ERRORS.has(result.error)) {
        logout();
      }
      toast({ title: 'Stato non aggiornato', description: result.message, variant: 'destructive' });
      return false;
    }

    if (action === 'delete') {
      setStaffList((prev) => prev.filter((member) => member.id !== staffId));
    } else {
      setStaffList((prev) => prev.map((member) => member.id === staffId ? result.data.staffMember : member));
    }
    toast({ title: 'Stato aggiornato', description: result.message });
    return true;
  }

  async function updateStaffInline(draft) {
    if (!canManageStaffDestructiveActions) return { ok: false, message: 'Permesso insufficiente.' };
    const savedMember = staffList.find((member) => member.id === draft.id);
    if (!savedMember || isInternalStaff(savedMember)) return { ok: false, message: 'Il profilo interno si modifica dalle proprie Impostazioni.' };
    const result = await updateStaffMember({ ...draft, tipoPersonale: 'esterno', linkedWorkspaceUserId: null });
    if (result.ok) setStaffList((current) => current.map((item) => item.id === draft.id ? result.data.staffMember : item));
    return result;
  }

  return {
    staffList,
    isLoadingStaff,
    staffUnavailable,
    staffAuthRequired,
    staffModuleDisabled,
    staffTenantDatabaseBlocked,
    staffError,
    staffAssignmentsStatus,
    staffAssignmentsError,
    staffCatalogStatus: staffAuthRequired
      ? 'auth_required'
      : staffModuleDisabled
        ? 'module_disabled'
      : staffTenantDatabaseBlocked
        ? 'tenant_database_unavailable'
      : staffUnavailable
        ? 'unavailable'
        : staffList.length > 0
          ? 'available'
          : 'empty',
    newStaff,
    setNewStaff,
    canCreateStaff,
    canManageStaffDestructiveActions,
    updateNewStaffField,
    resetNewStaff,
    createStaff,
    applyStaffLifecycle,
    updateStaffInline,
  };
}
